/** TR05: typed team contract. Ids remain opaque; labels are never join keys. */
import { ApiClientError, apiUrl, requestJson, responseArray, responseRecord, retryAfterSeconds } from './http';
export { ApiClientError } from './http';

export type GranteeKind = 'member' | 'role';
export type GrantScope = 'read_index' | 'request_transfer' | 'run_compute';
export type TargetKind = 'asset' | 'project';
export interface DeviceGrant {
  grantee_kind: GranteeKind;
  grantee_id: string;
  grantee_label: string;
  scopes: GrantScope[];
  granted_by_label: string;
  granted_at: string;
}
export interface DeviceGrants { grants: DeviceGrant[]; can_grant: boolean }
export interface GrantInput { grantee_kind: GranteeKind; grantee_id: string; scopes: GrantScope[] }
export interface LockTarget { target_kind: TargetKind; target_id: string }
export interface TeamLock extends LockTarget {
  holder_display_name: string;
  held_since: string;
  lease_expires_at: string;
  mine: boolean;
}
export interface AssetLockedDetail {
  holder_display_name: string;
  held_since: string;
  lease_expires_at: string;
}
export interface AuditEvent {
  event_id: string;
  event_kind: string;
  actor_label: string;
  subject_kind: string;
  subject_label: string;
  occurred_at: string;
  detail: Record<string, unknown>;
}
export interface AuditPage { events: AuditEvent[]; next_cursor: string | null }

export class AssetLockedError extends ApiClientError {
  constructor(readonly detail: AssetLockedDetail) {
    super('http', 409, 'asset_locked', 'The target is locked by another member.');
    this.name = 'AssetLockedError';
  }
}

/** Display-only privacy guard; never used to resolve or compare identities. */
export function safeTeamLabel(value: string, fallback = 'another member'): string {
  return !value || /[@/\\:.\x00-\x1f\x7f]/.test(value) ? fallback : value.slice(0, 120);
}

function malformed(): never {
  throw new ApiClientError('malformed', 200, 'malformed_response', 'The team response could not be read.');
}
function string(record: Record<string, unknown>, key: string): string {
  return typeof record[key] === 'string' ? record[key] as string : malformed();
}
function boolean(record: Record<string, unknown>, key: string): boolean {
  return typeof record[key] === 'boolean' ? record[key] as boolean : malformed();
}
function grant(payload: unknown): DeviceGrant {
  const row = responseRecord(payload);
  const kind = string(row, 'grantee_kind');
  if (kind !== 'member' && kind !== 'role') return malformed();
  const scopes = responseArray(row, 'scopes');
  if (!scopes.every((scope) => ['read_index', 'request_transfer', 'run_compute'].includes(scope as string))) return malformed();
  return { grantee_kind: kind, grantee_id: string(row, 'grantee_id'),
    grantee_label: string(row, 'grantee_label'), scopes: scopes as GrantScope[],
    granted_by_label: string(row, 'granted_by_label'), granted_at: string(row, 'granted_at') };
}
function lock(payload: unknown): TeamLock {
  const row = responseRecord(payload);
  const kind = string(row, 'target_kind');
  if (kind !== 'asset' && kind !== 'project') return malformed();
  return { target_kind: kind, target_id: string(row, 'target_id'),
    holder_display_name: string(row, 'holder_display_name'), held_since: string(row, 'held_since'),
    lease_expires_at: string(row, 'lease_expires_at'), mine: boolean(row, 'mine') };
}
const id = encodeURIComponent;
const root = '/v1/team';

/** http.requestJson cannot read 204 or retain TR-6 error details. Use its URL,
 * error and retry helpers for those mutations, with the same cookie/JSON rules.
 * The timeout covers body consumption as well as response headers. */
async function mutation(path: string, method: string, body?: unknown): Promise<unknown> {
  const controller = new AbortController();
  const timer = globalThis.setTimeout(() => controller.abort(), 15_000);
  try {
    const response = await fetch(apiUrl(path), { method, credentials: 'include', signal: controller.signal,
      headers: { accept: 'application/json', ...(body === undefined ? {} : { 'content-type': 'application/json' }) },
      ...(body === undefined ? {} : { body: JSON.stringify(body) }) });
    if (response.ok && response.status === 204) return undefined;
    let payload: unknown;
    try { payload = await response.json(); }
    catch { throw new ApiClientError(response.ok ? 'malformed' : 'http', response.status, 'request_failed', 'The team request could not be read.'); }
    if (!response.ok) {
      const record = payload && typeof payload === 'object' ? payload as Record<string, unknown> : {};
      const error = record.error && typeof record.error === 'object' ? record.error as Record<string, unknown> : {};
      if (response.status === 409 && error.code === 'asset_locked') {
        throw new AssetLockedError({ holder_display_name: string(error, 'holder_display_name'),
          held_since: string(error, 'held_since'), lease_expires_at: string(error, 'lease_expires_at') });
      }
      throw new ApiClientError('http', response.status, typeof error.code === 'string' ? error.code : 'request_failed',
        'The team request was refused.', retryAfterSeconds(response));
    }
    return payload;
  } catch (error) {
    if (error instanceof ApiClientError) throw error;
    if (controller.signal.aborted) throw new ApiClientError('timeout', 0, 'request_timeout', 'The team request timed out.');
    throw new ApiClientError('network', 0, 'network_error', 'The team service could not be reached.');
  } finally { globalThis.clearTimeout(timer); }
}

export async function fetchDeviceGrants(deviceId: string): Promise<DeviceGrants> {
  const row = responseRecord(await requestJson(`${root}/devices/${id(deviceId)}/grants`));
  return { grants: responseArray(row, 'grants').map(grant), can_grant: boolean(row, 'can_grant') };
}
export async function putDeviceGrant(deviceId: string, input: GrantInput): Promise<DeviceGrant> {
  return grant(await requestJson(`${root}/devices/${id(deviceId)}/grants`, { method: 'PUT', body: JSON.stringify(input) }));
}
export async function deleteDeviceGrant(deviceId: string, kind: GranteeKind, granteeId: string): Promise<void> {
  await mutation(`${root}/devices/${id(deviceId)}/grants/${id(kind)}/${id(granteeId)}`, 'DELETE');
}
export async function fetchTeamLocks(projectId: string): Promise<TeamLock[]> {
  return responseArray(responseRecord(await requestJson(`${root}/locks?${new URLSearchParams({ project_id: projectId })}`)), 'locks').map(lock);
}
export async function acquireTeamLock(input: LockTarget & { project_id: string; lease_seconds?: number }): Promise<TeamLock> {
  return lock(await mutation(`${root}/locks`, 'POST', input));
}
export async function renewTeamLock(target: LockTarget): Promise<TeamLock> {
  return lock(await mutation(`${root}/locks/renew`, 'POST', target));
}
export async function releaseTeamLock(target: LockTarget): Promise<TeamLock> {
  return lock(await mutation(`${root}/locks/release`, 'POST', target));
}
export async function stealTeamLock(target: LockTarget, reason: string): Promise<TeamLock> {
  return lock(await mutation(`${root}/locks/steal`, 'POST', { ...target, reason }));
}
export async function fetchTeamAudit(cursor?: string, limit = 50): Promise<AuditPage> {
  const params = new URLSearchParams({ limit: String(limit), ...(cursor === undefined ? {} : { cursor }) });
  const row = responseRecord(await requestJson(`${root}/audit?${params}`));
  const events = responseArray(row, 'events').map((item): AuditEvent => {
    const event = responseRecord(item);
    return { event_id: string(event, 'event_id'), event_kind: string(event, 'event_kind'),
      actor_label: string(event, 'actor_label'), subject_kind: string(event, 'subject_kind'),
      subject_label: string(event, 'subject_label'), occurred_at: string(event, 'occurred_at'), detail: responseRecord(event, 'detail') };
  });
  if (row.next_cursor !== null && typeof row.next_cursor !== 'string') return malformed();
  return { events, next_cursor: row.next_cursor as string | null };
}
